import { z } from "zod"

import { revisionSchema } from "./sync.ts"
import { holdReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * 1. Vehicle country code (ISO 3166-1 alpha-2).
 * Currently Australia ("AU") is the launched jurisdiction (PRD R4.1).
 */
export const countryCodeSchema = z.enum(["AU"])
export type CountryCode = z.infer<typeof countryCodeSchema>

/**
 * 2. Australian states and territories (8 codes).
 */
export const auRegistrationStateSchema = z.enum([
  "NSW",
  "VIC",
  "QLD",
  "WA",
  "SA",
  "TAS",
  "ACT",
  "NT",
])
export type AuRegistrationState = z.infer<typeof auRegistrationStateSchema>

/**
 * Helper to validate state code against country.
 */
export function isValidRegistrationState(countryCode: string, state: string): boolean {
  if (countryCode === "AU") {
    return auRegistrationStateSchema.safeParse(state).success
  }
  return true
}

/** Body types selectable on the vehicle form (vehicle-odometer-policy.md section 3). */
export const vehicleBodyTypes = [
  "sedan",
  "hatchback",
  "suv",
  "ute",
  "van",
  "wagon",
  "coupe",
  "truck",
  "motorcycle",
  "other",
] as const
export const vehicleBodyTypeSchema = z.enum(vehicleBodyTypes)
export type VehicleBodyType = z.infer<typeof vehicleBodyTypeSchema>

/**
 * Registration as written by clients: trimmed, upper-cased, 2-10 characters of letters and
 * digits (hyphen and space tolerated because legacy records and fixtures carry them).
 * The stored-entity schema stays lenient so old rows still round-trip.
 */
const registrationInputSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((value) => value.replace(/\s+/g, " "))
  .pipe(
    z
      .string()
      .min(2)
      .max(10)
      .regex(/^[A-Z0-9]+(?:[ -][A-Z0-9]+)*$/, "Registration must be letters and digits"),
  )

/** Request-side colour (strict). The stored schema stays lenient: DB `colour` is plain text. */
const colourSchema = z.string().trim().max(40)

/**
 * Vehicle entity schema as stored and synchronized.
 */
export const vehicleSchema = z
  .object({
    id: z.uuid(),
    organisation_id: z.uuid(),
    country_code: countryCodeSchema,
    registration_state: auRegistrationStateSchema,
    // Lenient on purpose (T146): DB columns are plain text/integer with no CHECK, so a legacy
    // empty or zero row must still parse instead of failing list and sync for the organisation.
    registration: z.string().trim(),
    make: z.string().trim(),
    model: z.string().trim(),
    year: z.number().int(),
    /** @deprecated Clients no longer send or show engine size; kept for /v1 compatibility. */
    engine_cc: z.number().int().nullable(),
    // Lenient on purpose: Postgres `colour` is plain text with no CHECK (T95 class of bug).
    colour: z.string().nullable().default(null),
    body_type: vehicleBodyTypeSchema.nullable().default(null),
    is_electric: z.boolean(),
    is_default: z.boolean(),
    /** `company` | `member_owned` (ADR-016 2.3); a plain string so a new value never breaks decoding. */
    ownership: z.string().optional(),
    /** The owning member of a `member_owned` vehicle; null otherwise. */
    owner_user_id: z.string().nullable().optional(),
    archived_at: z.string().datetime({ offset: true }).nullable(),
    visibility: tripVisibilitySchema.default("full"),
    hold_reason: holdReasonSchema.nullable().default(null),
    revision: revisionSchema,
    deleted_at: z.string().datetime({ offset: true }).nullable(),
    created_at: z.string().datetime({ offset: true }),
    updated_at: z.string().datetime({ offset: true }),
  })
  .refine((data) => !(data.is_electric && data.engine_cc !== null), {
    message: "Electric vehicles must have null engine_cc",
    path: ["engine_cc"],
  })

export type Vehicle = z.infer<typeof vehicleSchema>

/**
 * Request payload to create a new vehicle.
 */
export const createVehicleRequestSchema = z
  .object({
    id: z.uuid().optional(),
    country_code: countryCodeSchema.default("AU"),
    registration_state: auRegistrationStateSchema.default("NSW"),
    registration: registrationInputSchema,
    make: z.string().trim().min(1).max(60),
    model: z.string().trim().min(1).max(60),
    year: z.number().int(),
    /** @deprecated Clients no longer send engine size; accepted for legacy payloads. */
    engine_cc: z.number().int().positive().nullable().optional().default(null),
    colour: colourSchema.nullable().default(null),
    body_type: vehicleBodyTypeSchema.nullable().default(null),
    is_electric: z.boolean().optional().default(false),
    is_default: z.boolean().optional().default(false),
  })
  .refine((data) => !(data.is_electric && data.engine_cc !== null), {
    message: "Electric vehicles must have null engine_cc",
    path: ["engine_cc"],
  })

export type CreateVehicleRequest = z.input<typeof createVehicleRequestSchema>
export type ParsedCreateVehicleRequest = z.infer<typeof createVehicleRequestSchema>

/**
 * Request payload to update an existing vehicle (e.g. PATCH /api/v1/vehicles/:id).
 */
export const updateVehicleRequestSchema = z
  .object({
    base_revision: z.number().int().nonnegative().optional(),
    country_code: countryCodeSchema.optional(),
    registration_state: auRegistrationStateSchema.optional(),
    registration: registrationInputSchema.optional(),
    make: z.string().trim().min(1).max(60).optional(),
    model: z.string().trim().min(1).max(60).optional(),
    year: z.number().int().optional(),
    /** @deprecated Clients no longer send engine size; accepted for legacy payloads. */
    engine_cc: z.number().int().positive().nullable().optional(),
    colour: colourSchema.nullable().optional(),
    body_type: vehicleBodyTypeSchema.nullable().optional(),
    is_electric: z.boolean().optional(),
    is_default: z.boolean().optional(),
    archived_at: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .refine(
    (data) => !(data.is_electric && data.engine_cc !== null && data.engine_cc !== undefined),
    {
      message: "Electric vehicles must have null engine_cc",
      path: ["engine_cc"],
    },
  )

export type UpdateVehicleRequest = z.infer<typeof updateVehicleRequestSchema>
